import { queryOptions, useQuery } from "@tanstack/react-query";
import { timeKeys } from "@/lib/query";
import { getJson } from "@/lib/time/api-client";
import { type IsoDate, todayInZone, weekStartOf } from "@/lib/time/dates";
import { hoursLoggedOn } from "@/lib/time/today";
import type { MonthEntriesView, WeekView } from "@/schemas/time";

export function weekQueryOptions(weekStart: string) {
  return queryOptions({
    queryKey: timeKeys.week(weekStart),
    queryFn: () => getJson<WeekView>(`/api/time/week?start=${encodeURIComponent(weekStart)}`),
  });
}

/** One Sun-Sat week of the person's time with plans and periods (GET /api/time/week). */
export function useTimeWeek(weekStart: string, options: { enabled?: boolean } = {}) {
  return useQuery({ ...weekQueryOptions(weekStart), enabled: options.enabled ?? true });
}

export function monthEntriesQueryOptions(month: string) {
  return queryOptions({
    queryKey: timeKeys.monthEntries(month),
    queryFn: () =>
      getJson<MonthEntriesView>(`/api/time/month-entries?month=${encodeURIComponent(month)}`),
  });
}

/** Every entry of the person in one calendar month (the Entries Month view). */
export function useMonthEntries(month: string, options: { enabled?: boolean } = {}) {
  return useQuery({ ...monthEntriesQueryOptions(month), enabled: options.enabled ?? true });
}

/** Today in America/New_York (the app's clock) and the Sunday of its week. */
export function todayWeek(now: Date): { today: IsoDate; weekStart: IsoDate } {
  const today = todayInZone(now);
  return { today, weekStart: weekStartOf(today) };
}

/**
 * Hours logged today, from this week's view (the same query the Time grid reads, so any entry
 * write refreshes it). Null while it loads or when it failed.
 */
export function useTodayHours(): { today: IsoDate; hours: number | null } {
  const { today, weekStart } = todayWeek(new Date());
  const week = useTimeWeek(weekStart);
  return { today, hours: week.data ? hoursLoggedOn(week.data.entries, today) : null };
}
